import { useFormik } from "formik";
import { Modal, Form, Button, InputGroup } from "react-bootstrap";
import DateTime from "react-datetime";
import Select from "react-select";
import CurrencyCustomInput from "../utils/CurrencyCustomInput";
import NumberCustomInput from "../utils/NumberCustomInput";
import * as yup from "yup";
import { useAuth } from "../../hooks/useAuth";
import { useQuery, useMutation } from "react-query";
import { useCallback, useMemo, useState } from "react";
import currency from "currency.js";
import { toast } from "react-toastify";
import ModalLoader from "../utils/ModalLoader";
import { Popover } from "react-tiny-popover";
import ItemsTable from "../utils/ItemsTable";
import MagnifyIcon from "mdi-react/MagnifyIcon";
import ConfirmDialog from "../ConfirmDialogue";
import { atcTypeOptions, nanoid } from "../../utils/helpers";
import { isEmpty } from "lodash";
import CogSyncOutlineIcon from "mdi-react/CogSyncOutlineIcon";

const paymentMethod = [
  // {
  //   label: "CASH",
  //   value: "CASH",
  // },
  {
    label: "CASH (DMS)",
    value: "CASH (DMS)",
  },
  // {
  //   label: "CLEAN CREDIT",
  //   value: "CLEAN CREDIT",
  // },
  {
    label: "BANK GUARANTEE",
    value: "BANK GUARANTEE",
  },
  {
    label: "OVER DRAFT",
    value: "OVER DRAFT",
  },
];

const paymentTerms = [
  {
    label: "One Installment",
    value: 1,
  },
  {
    label: "Two Installments",
    value: 2,
  },
  {
    label: "Three Installments",
    value: 3,
  },
  {
    label: "Four Installments",
    value: 4,
  },
  {
    label: "Five Installments",
    value: 5,
  },
  {
    label: "Six Installments",
    value: 6,
  },
  {
    label: "Seven Installments",
    value: 7,
  },
  {
    label: "Eight Installments",
    value: 8,
  },

  {
    label: "Nine Installments",
    value: 9,
  },
  {
    label: "Ten Installments",
    value: 10,
  },
];

const getSubCategories = (parentCat = "All", subCategories = []) => {
  subCategories = subCategories
    .filter(el => el.Cat_Name === parentCat)
    .map(el => ({ label: el.Sub_Cat, value: el.Sub_Cat }));
  return [{ label: "All", value: "All" }, ...subCategories];
};

export default function SetupAtcModal({
  showSetupAtcModal,
  setShowSetupAtcModal,
  initialValues,
  refetch: refetchAtcModal,
}) {
  const [showItemsPopover, setShowItemsPopover] = useState(false);
  const { backendUrl } = useAuth();

  const formik = useFormik({
    initialValues: {
      Bar_Code: initialValues?.Bar_Code || "",
      Item_Name: initialValues?.Item_Name || "",
      requestDate: new Date(),
      Qty: initialValues?.Qty || 0,
      Vendor_ID: initialValues?.Vendor_ID || "",
      Region: initialValues?.Region || "",
      Plant: initialValues?.Plant || "",
      Depot: initialValues?.Depot || "",
      PaymentType: initialValues?.PaymentType || "",
      amountPaid: initialValues?.amountPaid || 0,
      UnitCost: initialValues?.UnitCost || 0,
      Product_Name: initialValues?.Product_Name || "None",
      Cat_Name: initialValues?.Cat_Name || "None",
      Sub_Cat: initialValues?.Sub_Cat || "None",
      payTerm: initialValues?.payTerm || 1,
      nextInstallment: initialValues?.nextInstallment || 45,
      bank: initialValues?.bank || "",
      Trans_ID: initialValues?.Trans_ID || "",
      ATC_Type: initialValues?.ATC_Type || "",

      //
      incentiveId: "",
      promoIncentiveId: "",
    },
    validationSchema: yup.object().shape({
      Trans_ID: yup.string().required("required"),
      Qty: yup.number().required("required"),
      UnitCost: yup.number().required("required"),
      Vendor_ID: yup.string().required("required"),
      Region: yup.string().required("required"),
      PaymentType: yup.string().required("required"),
      Cat_Name: yup.string().required("required"),
      Sub_Cat: yup.string().required("required"),
      // bank: yup.string().required("required"),

      incentiveId: yup.string().required("required"),
      promoIncentiveId: yup.string().required("required"),
      ATC_Type: yup.string().required("required"),
    }),
    onSubmit: async values => {
      //
      if (isEmpty(values?.ATC_Type)) {
        return toast.error("ATC type is required");
      }

      //
      if (!values.Qty || Number(values.Qty) <= 0) {
        return toast.error("Qty is required");
      }

      //
      if (!values.UnitCost || Number(values.UnitCost) <= 0) {
        return toast.error("UnitCost is required");
      }

      //
      if (
        !values.bank &&
        ["CASH (DMS)", "BANK GUARANTEE", "OVERDRAFT"].includes(
          values.PaymentType
        )
      ) {
        return toast.error(`Bank is required for ${values.PaymentType}`);
      }

      // Select plant or depot
      if (values.Plant === "" && values.Depot === "") {
        formik.setFieldError("Plant", "Please select Plant or Depot");
        formik.setFieldError("Depot", "Please select Plant or Depot");
        return;
      }

      //
      const proceed = await ConfirmDialog({
        title: "Confirm ATC Setup",
        message: `Are you sure you want to set up ATC for ${
          values.Item_Name
        } (${values.Bar_Code})?`,
      });

      if (!proceed) {
        return;
      }

      const formDataToSave = { ...values };
      formDataToSave.totalAmount = totalAmount;
      createAtcMutation.mutate(formDataToSave);
    },
  });

  const fetchSetUpData = async () => {
    // await waitFor(5000);
    let response = await fetch(`${backendUrl}/api/incentive/set-up`, {
      method: "GET",
      headers: {
        Accept: "Application/json",
        "Content-Type": "Application/json",
      },
      credentials: "include",
    });
    if (!response.ok) {
      response = await response.json();
      throw new Error(response.message);
    }
    const { data } = await response.json();

    data.vendorsSelect = data.vendors
      .filter(el => el.Type !== "Bank")
      .map(el => ({
        label: el.CompanyName,
        value: el.Vendor_ID,
      }));

    data.plantSelect = data.plant.map(el => ({
      region: el.Region,
      label: el.Plant,
      value: el.Plant,
    }));

    data.depotSelect = data.depot.map(el => ({
      region: el.Region,
      label: el.Depot,
      value: el.Depot,
    }));

    data.regionSelect = data.region.map(el => ({
      label: el.region,
      value: el.region,
    }));

    data.category = [
      {
        label: "All",
        value: "All",
      },
      ...data.categories.map(el => ({
        ...el,
        value: el.Cat_Name,
        label: el.Cat_Name,
      })),
    ];

    data.subcategory = [
      {
        label: "All",
        value: "All",
      },
      ...data.subCategories.map(el => ({
        ...el,
        value: el.Sub_Cat,
        label: el.Sub_Cat,
      })),
    ];

    data.banks = data?.banks.map(el => ({
      ...el,
      value: el.BankName,
      label: el.BankName,
    }));

    data.vendorBanks = data.vendorBanks.map(el => ({
      ...el,
      value: el.CompanyName,
      label: el.CompanyName,
    }));

    data.incentives = [
      {
        label: "None",
        value: "None",
      },
      ...data.incentives.map(el => {
        return {
          ...el,
          value: el.id,
          label:
            el.IncentiveType === "Promo"
              ? `${el.Name}`
              : `${el.Name} - ${currency(el.Amount, {
                  symbol: "",
                }).format()} - ${el.RewardBy}`,
        };
      }),
    ];

    return data;
  };

  const {
    data = {
      category: [],
      subcategory: [],
      region: [],
      depot: [],
      plant: [],
      subCategoriesWithParent: [],
      plantAndDepot: [],
      // -------------------------
      vendorsSelect: [],
      plantSelect: [],
      depotSelect: [],
      regionSelect: [],
      banks: [],
      vendorBanks: [],
      incentives: [],
    },
    refetch,
  } = useQuery(["INCENTIVE_SETUP"], () => fetchSetUpData(), {
    keepPreviousData: true,
  });

  const filterByRegion = (region = "", data = []) => {
    return data.filter(el => el.region === region);
  };

  const bankSelectOptions = paymentMethod => {
    return ["OVER DRAFT", "CASH (DMS)", "BANK GUARANTEE"].includes(
      paymentMethod
    )
      ? /*data.vendorBanks */ data.banks
      : [];
  };

  const totalAmount = useMemo(() => {
    return currency(formik.values.UnitCost).multiply(formik.values.Qty).value;
  }, [formik.values.Qty, formik.values.UnitCost]);

  const createAtc = async payload => {
    let response = await fetch(`${backendUrl}/api/atc/setup-atc`, {
      method: "POST",
      credentials: "include",
      body: JSON.stringify(payload),
      headers: {
        Accept: "Application/json",
        "Content-Type": "Application/json",
      },
    });
    if (!response.ok) {
      response = await response.json();
      throw new Error(response.message);
    }
    const res = await response.json();
    return res;
  };
  const createAtcMutation = useMutation(payload => createAtc(payload), {
    onSuccess: ({ message, data }) => {
      if (refetchAtcModal) refetchAtcModal();
      toast.success(message);
      formik.resetForm();
      setShowSetupAtcModal(false);
    },
    onError: error => {
      toast.error(`Unable to perform action: \n ${error.message}`);
    },
  });

  const handlePaymentMethod = selected => {
    formik.setFieldValue("PaymentType", selected.value);
    formik.setFieldValue("bank", "");
    if (["CASH", "CASH (DMS)"].includes(selected.value)) {
      formik.setFieldValue("amountPaid", totalAmount);
    }
  };

  const handleSelectedItem = useCallback(
    async item => {
      // Validate item
      if (item?.Item_Type === "Service") {
        toast.error("Service items are not allowed for ATC setup");
        return;
      }

      if (initialValues?.Bar_Code && initialValues.Bar_Code !== item.Bar_Code) {
        toast.error("You cannot change the item for this ATC setup");
        return;
      }

      formik.setFieldValue("Bar_Code", item.Bar_Code, true);
      formik.setFieldValue("Item_Name", item.Item_Name, true);
      formik.setFieldValue("Region", item.Region);
      formik.setFieldValue("Plant", item.Plant);
      formik.setFieldValue("Depot", item.Depot);
      formik.setFieldValue("Cat_Name", item?.Cat_Name || "None");
      formik.setFieldValue("Sub_Cat", item?.Sub_Cat || "None");
      formik.setFieldValue("Product_Name", item?.Product_Name || "None");
      setShowItemsPopover(false);
    },
    [initialValues?.Bar_Code, formik.setFieldValue, setShowItemsPopover]
  );

  const itemsTableContent = useCallback(
    () => (
      <ItemsTable
        handleSelectedItem={handleSelectedItem}
        Item_Type="Inventory"
      />
    ),
    [handleSelectedItem]
  );

  return (
    <>
      <Modal
        size="lg"
        show={showSetupAtcModal}
        onHide={() => setShowSetupAtcModal(false)}
        animation={false}
        centered
        autoFocus={false}
        enforceFocus={false}
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title
            className="h5 d-flex align-items-center flex-column gap-2"
            id="example-modal-sizes-title-lg"
          >
            {initialValues?.requestid ? "Raise PO" : "ATC Type / Stock setup"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form noValidate onSubmit={formik.handleSubmit} autoComplete="off">
            <div className="row gap-4">
              <div className="col">
                <Form.Group className="mb-3 pb-2">
                  <Form.Label className="mb-1">Item Code</Form.Label>
                  <InputGroup>
                    <Form.Control
                      className=""
                      type="text"
                      placeholder="Enter Item Code"
                      name="Bar_Code"
                      value={formik.values.Bar_Code}
                      onChange={formik.handleChange}
                      isInvalid={
                        formik.touched.Bar_Code && !!formik.errors.Bar_Code
                      }
                      disabled={true}
                    />

                    <Popover
                      isOpen={showItemsPopover}
                      onClickOutside={() => setShowItemsPopover(false)}
                      content={itemsTableContent}
                      position="bottom"
                    >
                      <InputGroup.Text
                        onClick={() => setShowItemsPopover(!showItemsPopover)}
                      >
                        <MagnifyIcon />
                      </InputGroup.Text>
                    </Popover>
                  </InputGroup>

                  <Form.Control.Feedback type="invalid">
                    {formik.errors.Bar_Code}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-3 pb-2">
                  <Form.Label className="mb-1">Item Name</Form.Label>
                  <Form.Control
                    className=""
                    type="text"
                    placeholder="Enter Item Name"
                    name="Item_Name"
                    value={formik.values.Item_Name}
                    onChange={formik.handleChange}
                    isInvalid={
                      formik.touched.Item_Name && !!formik.errors.Item_Name
                    }
                    readOnly
                    disabled={initialValues?.Item_Name ? true : false}
                    onClick={() => setShowItemsPopover(!showItemsPopover)}
                  />
                </Form.Group>

                <Form.Group className="mb-3 pb-2">
                  <Form.Label className="mb-1">Request Date</Form.Label>
                  <DateTime
                    timeFormat={false}
                    closeOnSelect={true}
                    closeOnClickOutside={true}
                    dateFormat="MMM DD, YYYY"
                    inputProps={{
                      className: `date-input form-control`,
                      placeholder: "Select date",
                      readOnly: true,
                    }}
                    value={formik.values.requestDate}
                    onChange={date => {
                      formik.setFieldValue("requestDate", date, true);
                    }}
                  />
                </Form.Group>

                <Form.Group className="mb-3 pb-2">
                  <Form.Label className="mb-1">
                    Ticket Number / Ref Number
                  </Form.Label>
                  <InputGroup
                    isInvalid={
                      formik.touched.Trans_ID && !!formik.errors.Trans_ID
                    }
                  >
                    <Form.Control
                      name="Trans_ID"
                      title="Ref Number"
                      placeholder="Enter Ticket Number / Ref Number"
                      value={formik.values.Trans_ID}
                      onChange={formik.handleChange}
                      isInvalid={
                        formik.touched.Trans_ID && !!formik.errors.Trans_ID
                      }
                      disabled={initialValues?.Trans_ID ? true : false}
                    />
                    <InputGroup.Text
                      title="Generate"
                      onClick={() =>
                        formik.setFieldValue("Trans_ID", nanoid(12, "number"))
                      }
                    >
                      <CogSyncOutlineIcon />
                    </InputGroup.Text>
                  </InputGroup>

                  <Form.Control.Feedback type="invalid">
                    {formik.errors.Trans_ID}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-3 pb-2">
                  <Form.Label className="mb-1">Region</Form.Label>
                  <Select
                    classNamePrefix={"form-select"}
                    options={data?.regionSelect}
                    className={
                      formik.touched.Region && !!formik.errors.Region
                        ? "is-invalid"
                        : ""
                    }
                    isSearchable={false}
                    value={
                      (data?.regionSelect || []).find(
                        el => el.value === formik.values.Region
                      ) || ""
                    }
                    onChange={selected => {
                      formik.setFieldValue("Region", selected.value);
                    }}
                    //  isDisabled={initialValues?.Region ? true : false}
                    isDisabled={true}
                  />
                  {formik.touched.Region && !!formik.errors.Region ? (
                    <span className="text-danger mt-2">
                      {formik.errors.Region}
                    </span>
                  ) : null}
                </Form.Group>

                <Form.Group className="mb-3 pb-2">
                  <Form.Label className="mb-1">Plant</Form.Label>
                  <Select
                    classNamePrefix={"form-select"}
                    options={filterByRegion(
                      formik.values.Region,
                      data?.plantSelect
                    )}
                    className={
                      formik.touched.Plant && !!formik.errors.Plant
                        ? "is-invalid"
                        : ""
                    }
                    isSearchable={false}
                    value={
                      (
                        filterByRegion(
                          formik.values.Region,
                          data?.plantSelect
                        ) || []
                      ).find(el => el.value === formik.values.Plant) || ""
                    }
                    onChange={selected => {
                      formik.setFieldValue("Plant", selected.value);
                      formik.setFieldValue("Depot", "");
                    }}
                    //   isDisabled={initialValues?.Plant ? true : false}
                    isDisabled={true}
                  />
                  {formik.touched.Plant && !!formik.errors.Plant ? (
                    <span className="text-danger mt-2">
                      {formik.errors.Plant}
                    </span>
                  ) : null}
                </Form.Group>

                <Form.Group className="mb-3 pb-2">
                  <Form.Label className="mb-1">Depot</Form.Label>
                  <Select
                    classNamePrefix={"form-select"}
                    options={filterByRegion(
                      formik.values.Region,
                      data?.depotSelect
                    )}
                    className={
                      formik.touched.Depot && !!formik.errors.Depot
                        ? "is-invalid"
                        : ""
                    }
                    isSearchable={false}
                    value={
                      (
                        filterByRegion(
                          formik.values.Region,
                          data?.depotSelect
                        ) || []
                      ).find(el => el.value === formik.values.Depot) || ""
                    }
                    onChange={selected => {
                      formik.setFieldValue("Depot", selected.value);
                      formik.setFieldValue("Plant", "");
                    }}
                    //  isDisabled={initialValues?.Depot ? true : false}
                    isDisabled={true}
                  />
                  {formik.touched.Depot && !!formik.errors.Depot ? (
                    <span className="text-danger mt-2">
                      {formik.errors.Depot}
                    </span>
                  ) : null}
                </Form.Group>
              </div>

              <div className="col">
                <Form.Group className="mb-3 pb-2">
                  <Form.Label className="mb-1">Vendor</Form.Label>
                  <Select
                    classNamePrefix={"form-select"}
                    options={data?.vendorsSelect}
                    className={
                      formik.touched.Vendor_ID && !!formik.errors.Vendor_ID
                        ? "is-invalid"
                        : ""
                    }
                    isSearchable={false}
                    value={
                      (data?.vendorsSelect || []).find(
                        el => el.value === formik.values.Vendor_ID
                      ) || ""
                    }
                    onChange={selected => {
                      formik.setFieldValue("Vendor_ID", selected.value);
                    }}
                    // isDisabled={[
                    //   "OVER DRAFT",
                    //   "CLEAN CREDIT",
                    //   "BANK GUARANTEE",
                    // ].includes(formik.values.PaymentType)}

                    isDisabled={initialValues?.Vendor_ID ? true : false}
                  />
                  {formik.touched.Vendor_ID && !!formik.errors.Vendor_ID ? (
                    <span className="text-danger mt-2">
                      {formik.errors.Vendor_ID}
                    </span>
                  ) : null}
                </Form.Group>

                <Form.Group className="mb-3 pb-2">
                  <Form.Label className="mb-1">Quantity in Bags</Form.Label>
                  <NumberCustomInput
                    placeholder="0"
                    name="Qty"
                    value={formik.values.Qty}
                    onValueChange={(value, name) => {
                      formik.setFieldValue(name, value, true);
                    }}
                    isInvalid={formik.touched.Qty && !!formik.errors.Qty}
                    onBlur={() => formik.setFieldTouched("Qty", true)}
                    allowNegativeValue={false}
                    disabled={initialValues?.Qty ? true : false}
                  />
                  {formik.touched.Qty && !!formik.errors.Qty ? (
                    <span className="custom-invalid-feedback">
                      {formik.errors.Qty}
                    </span>
                  ) : null}
                </Form.Group>

                <Form.Group className="mb-3 pb-2">
                  <Form.Label className="mb-1">Unit Cost</Form.Label>
                  <CurrencyCustomInput
                    name="UnitCost"
                    value={formik.values.UnitCost}
                    onValueChange={(value, name) => {
                      formik.setFieldValue(name, value);
                    }}
                    isInvalid={
                      formik.touched.UnitCost && !!formik.errors.UnitCost
                    }
                    placeholder="0.00"
                    disabled={initialValues?.UnitCost ? true : false}
                  />
                  {formik.touched.UnitCost && !!formik.errors.UnitCost ? (
                    <span className="custom-invalid-feedback">
                      {formik.errors.UnitCost}
                    </span>
                  ) : null}
                </Form.Group>

                <Form.Group className="mb-3 pb-2">
                  <Form.Label className="mb-1">Total Amount</Form.Label>
                  <CurrencyCustomInput value={totalAmount} readOnly />
                </Form.Group>

                <Form.Group className="mb-3 pb-2">
                  <Form.Label className="mb-1">
                    Manufacturer / Product
                  </Form.Label>
                  <Select
                    classNamePrefix={"form-select"}
                    options={[]}
                    value={{
                      label: formik.values.Product_Name,
                      value: formik.values.Product_Name,
                    }}
                    isDisabled={true}
                  />
                </Form.Group>

                <Form.Group className="mb-3 pb-2">
                  <Form.Label className="mb-1">Category</Form.Label>
                  <Select
                    classNamePrefix={"form-select"}
                    options={data?.category}
                    value={
                      data?.category?.find(
                        el => el.value === formik.values.Cat_Name
                      ) || ""
                    }
                    onChange={({ value }) => {
                      formik.setFieldValue("Cat_Name", value);
                      formik.setFieldValue("Sub_Cat", "None");
                    }}
                    className={
                      formik.touched.Cat_Name && !!formik.errors.Cat_Name
                        ? "is-invalid"
                        : ""
                    }
                    // isDisabled={initialValues?.Cat_Name ? true : false}
                    isDisabled={true}
                  />
                  {formik.touched.Cat_Name && !!formik.errors.Cat_Name ? (
                    <span className="text-danger mt-2">
                      {formik.errors.Cat_Name}
                    </span>
                  ) : null}
                </Form.Group>

                <Form.Group className="mb-3 pb-2">
                  <Form.Label className="mb-1">Sub Category</Form.Label>
                  <Select
                    classNamePrefix={"form-select"}
                    options={getSubCategories(
                      formik.values.Cat_Name,
                      data?.subCategoriesWithParent
                    )}
                    value={
                      getSubCategories(
                        formik.values.Cat_Name,
                        data?.subCategoriesWithParent
                      ).find(el => el.value === formik.values.Sub_Cat) || ""
                    }
                    onChange={({ value }) =>
                      formik.setFieldValue("Sub_Cat", value)
                    }
                    className={
                      formik.touched.Sub_Cat && !!formik.errors.Sub_Cat
                        ? "is-invalid"
                        : ""
                    }
                    //  isDisabled={initialValues?.Sub_Cat ? true : false}
                    isDisabled={true}
                  />
                  {formik.touched.Sub_Cat && !!formik.errors.Sub_Cat ? (
                    <span className="text-danger mt-2">
                      {formik.errors.Sub_Cat}
                    </span>
                  ) : null}
                </Form.Group>

                <Form.Group className="mb-3 pb-2">
                  <Form.Label className="mb-1">ATC Type</Form.Label>
                  <Select
                    classNamePrefix={"form-select"}
                    options={atcTypeOptions}
                    value={
                      formik.values?.ATC_Type
                        ? {
                            label: formik.values?.ATC_Type,
                            value: formik.values?.ATC_Type,
                          }
                        : null
                    }
                    onChange={selected => {
                      formik.setFieldValue("ATC_Type", selected?.value);
                    }}
                    placeholder={"Select ATC Type"}
                  />
                  {formik.touched.ATC_Type && !!formik.errors.ATC_Type ? (
                    <span className="text-danger mt-2">
                      {formik.errors.ATC_Type}
                    </span>
                  ) : null}
                </Form.Group>
              </div>
            </div>

            <hr />

            <div className="row gap-4">
              <div className="col">
                <Form.Group className="mb-3 pb-2">
                  <Form.Label className="mb-1">Regular Incentive</Form.Label>
                  <Select
                    className="z-10"
                    classNamePrefix={"form-select"}
                    isSearchable={false}
                    value={data?.incentives.find(
                      el => el.value === formik.values.incentiveId
                    )}
                    options={data?.incentives.filter(
                      el => el.IncentiveType !== "Promo"
                    )}
                    onChange={selected =>
                      formik.setFieldValue("incentiveId", selected?.value)
                    }
                    menuPosition="fixed"
                  />
                  {formik.touched.incentiveId && !!formik.errors.incentiveId ? (
                    <span className="text-danger mt-2">
                      {formik.errors.incentiveId}
                    </span>
                  ) : null}
                </Form.Group>
              </div>

              <div className="col">
                <Form.Group className="mb-3 pb-2">
                  <Form.Label className="mb-1">Promo Incentive</Form.Label>
                  <Select
                    className="z-10"
                    classNamePrefix={"form-select"}
                    isSearchable={false}
                    value={data?.incentives.find(
                      el => el.value === formik.values.promoIncentiveId
                    )}
                    options={data?.incentives.filter(
                      el =>
                        el.IncentiveType === "Promo" ||
                        ["None", ""].includes(el.value)
                    )}
                    onChange={selected =>
                      formik.setFieldValue("promoIncentiveId", selected?.value)
                    }
                    menuPosition="fixed"
                  />
                  {formik.touched.promoIncentiveId &&
                  !!formik.errors.promoIncentiveId ? (
                    <span className="text-danger mt-2">
                      {formik.errors.promoIncentiveId}
                    </span>
                  ) : null}
                </Form.Group>
              </div>
            </div>

            <hr />

            <div className="row gap-4">
              <div className="col">
                <Form.Group className="mb-3 pb-2">
                  <Form.Label className="mb-1">Payment Method</Form.Label>
                  <Select
                    classNamePrefix={"form-select"}
                    options={paymentMethod}
                    className={
                      formik.touched.PaymentType && !!formik.errors.PaymentType
                        ? "is-invalid"
                        : ""
                    }
                    isSearchable={false}
                    value={
                      (paymentMethod || []).find(
                        el => el.value === formik.values.PaymentType
                      ) || ""
                    }
                    onChange={selected => handlePaymentMethod(selected)}
                  />
                  {formik.touched.PaymentType && !!formik.errors.PaymentType ? (
                    <span className="text-danger mt-2">
                      {formik.errors.PaymentType}
                    </span>
                  ) : null}
                </Form.Group>

                <Form.Group className="mb-3 pb-2">
                  <Form.Label className="mb-1">Bank</Form.Label>
                  <Select
                    classNamePrefix={"form-select"}
                    options={bankSelectOptions(formik.values.PaymentType)}
                    value={
                      bankSelectOptions(formik.values.PaymentType).find(
                        el => el.value === formik.values.bank
                      ) || ""
                    }
                    onChange={selected => {
                      // if (selected?.Vendor_ID) {
                      //   if (
                      //     initialValues?.Vendor_ID &&
                      //     initialValues.Vendor_ID !== selected.Vendor_ID
                      //   ) {
                      //     toast.error(
                      //       "Selected atc bank does not belong to the selected vendor"
                      //     );
                      //     return;
                      //   }
                      // }

                      //
                      formik.setFieldValue("bank", selected.value);

                      // if (selected?.Vendor_ID) {
                      //   formik.setFieldValue("Vendor_ID", selected.Vendor_ID);
                      // }
                    }}
                    className={
                      formik.touched.bank && !!formik.errors.bank
                        ? "is-invalid"
                        : ""
                    }
                    isDisabled={[
                      "CASH",
                      "CLEAN CREDIT" /* "CASH (DMS)" */,
                    ].includes(formik.values.PaymentType)}
                  />
                  {formik.touched.bank && !!formik.errors.bank ? (
                    <span className="text-danger mt-2">
                      {formik.errors.bank}
                    </span>
                  ) : null}
                </Form.Group>

                <Form.Group className="mb-3 pb-2">
                  <Form.Label className="mb-1">Amount Paid</Form.Label>
                  <CurrencyCustomInput
                    name="amountPaid"
                    value={formik.values.amountPaid}
                    onValueChange={(value, name) => {
                      formik.setFieldValue(name, value);
                    }}
                    isInvalid={
                      formik.touched.amountPaid && !!formik.errors.amountPaid
                    }
                    placeholder="0.00"
                    isDisabled={
                      !["CASH", "CASH (DMS)"].includes(
                        formik.values.PaymentType
                      )
                    }
                  />
                  {formik.touched.amountPaid && !!formik.errors.amountPaid ? (
                    <span className="custom-invalid-feedback">
                      {formik.errors.amountPaid}
                    </span>
                  ) : null}
                </Form.Group>
              </div>

              <div className="col">
                <Form.Group className="mb-3 pb-2">
                  <Form.Label className="mb-1">Payment term</Form.Label>
                  <Select
                    classNamePrefix={"form-select"}
                    options={paymentTerms}
                    value={
                      paymentTerms?.find(
                        el => el.value === formik.values.payTerm
                      ) || ""
                    }
                    onChange={({ value }) => {
                      formik.setFieldValue("payTerm", value);
                    }}
                  />
                </Form.Group>

                <Form.Group className="mb-3 pb-2">
                  <Form.Label className="mb-1">
                    Next Installment (days)
                  </Form.Label>
                  <NumberCustomInput
                    name="nextInstallment"
                    value={formik.values.nextInstallment}
                    onValueChange={(value, name) => {
                      formik.setFieldValue(name, value);
                    }}
                  />
                </Form.Group>
              </div>
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer className="gap-3">
          {/* <Button onClick={() => formik.resetForm()} variant="outline-primary">
            Refresh
          </Button> */}

          <Button
            onClick={() => setShowSetupAtcModal(false)}
            variant="outline-primary"
          >
            Cancel
          </Button>
          <Button onClick={() => formik.submitForm()} className="px-4">
            Post
          </Button>
        </Modal.Footer>
      </Modal>
      <ModalLoader show={createAtcMutation.isLoading} />
    </>
  );
}
